import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Loader2, Sparkles, AlertCircle, FilePlus2 } from "lucide-react";
import api from "@/lib/api";
import { friendlyStatus } from "@/lib/citizen";
import { relativeTime } from "@/lib/utils-app";

export default function Projects() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/crm/cases").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = items.filter((c) =>
    !q || c.title.toLowerCase().includes(q.toLowerCase()) || c.address?.toLowerCase().includes(q.toLowerCase())
  );
  const action = items.filter((c) => c.status === "komplettering");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Min sida</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
            Mina projekt
          </h1>
        </div>
        <div className="flex gap-2">
          <Link to="/ansok" data-testid="proj-new"
            className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2.5 rounded-sm text-sm font-medium">
            <Plus className="w-4 h-4" /> Ny ansökan
          </Link>
        </div>
      </div>

      {action.length > 0 && (
        <div className="mt-6 bg-amber-50 border border-amber-300 rounded-sm p-4 flex items-center gap-3" data-testid="proj-action-banner">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">{action.length} projekt väntar på dig</div>
            <div className="text-xs text-amber-800">Handläggaren har begärt kompletteringar.</div>
          </div>
          <Link to={`/cases/${action[0].case_id}`} className="text-sm font-medium text-amber-900 hover:underline">Öppna →</Link>
        </div>
      )}

      <div className="mt-6 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <input
          data-testid="proj-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Sök projekt..."
          className="w-full pl-10 pr-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm bg-white"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="mt-8 bg-white border border-zinc-200 rounded-sm py-16 text-center">
          <FilePlus2 className="w-10 h-10 mx-auto text-zinc-300" />
          <div className="mt-3 text-sm text-zinc-600">Du har inga projekt ännu.</div>
          <Link to="/ansok" data-testid="proj-empty-cta" className="mt-4 inline-block text-sm text-blue-700 hover:underline">
            Starta din första ansökan →
          </Link>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => {
            const s = friendlyStatus(c.status);
            return (
              <Link
                key={c.case_id}
                to={`/cases/${c.case_id}`}
                data-testid={`proj-${c.case_id}`}
                className="group bg-white border border-zinc-200 rounded-sm p-5 hover:border-blue-400 hover:-translate-y-[1px] transition"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-1 self-stretch ${s.dot} rounded-full`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{c.ref}</div>
                    <h3 className="mt-1 font-medium text-zinc-900 group-hover:text-blue-700 transition truncate">{c.title}</h3>
                    <div className="text-xs text-zinc-500 truncate">{c.address}</div>
                    <div className="mt-3 flex items-center gap-2">
                      <span className={`px-2 py-0.5 text-[11px] font-medium border rounded-sm ${s.classes}`}>{s.short}</span>
                      <span className="text-[11px] text-zinc-400">{relativeTime(c.updated_at)}</span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
